import type { PlanSummary } from '@rheora/enrollment-contract';

/**
 * ZIP → delivery utility (TDU) resolution for the OPTIONAL shelf filter (E-SF10).
 *
 * ⚠️ DEMO REFERENCE DATA — this is NOT the real ERCOT ZIP→TDU directory. The map
 * mirrors the seeded serviceable ZIPs (the same set as the BFF address catalogue
 * and `seed.market-ops.ts`); production replaces it with the commercial / ERCOT
 * ZIP→TDU lookup. Importantly, the plans being FILTERED still come from the
 * single source of truth (Pricing via `/bff/plans`) — only this ZIP→area lookup
 * is demo config, and it filters the real catalogue rather than inventing data.
 *
 * The TDU codes here MATCH the `tdsp` code each plan carries (ONCR / CNP / AEP-C
 * / AEP-N), so `filterPlansByTdu` is an exact-code match against real catalogue
 * rows.
 */
const ZIP_TO_TDU: Readonly<Record<string, string>> = {
  // Oncor — DFW metro
  '75080': 'ONCR',
  '75201': 'ONCR',
  '75202': 'ONCR',
  '75024': 'ONCR',
  '75038': 'ONCR',
  '76101': 'ONCR',
  '75056': 'ONCR',
  // CenterPoint — Houston metro
  '77002': 'CNP',
  '77003': 'CNP',
  '77019': 'CNP',
  '77479': 'CNP',
  '77056': 'CNP',
  '77057': 'CNP',
  // AEP Texas Central (Corpus Christi) / North (Abilene)
  '78401': 'AEP-C',
  '79601': 'AEP-N',
};

/**
 * TDU code → the name a customer would recognise (E-SF19 P2).
 *
 * ⚠️ IT MUST COVER EVERY CODE THE CATALOGUE CAN RETURN. It had four entries while
 * `/bff/plans` serves six (ONCR, CNP, AEP-C, AEP-N, TNMP, LPL — confirmed live
 * 2026-08-26), so TNMP and LPL fell through the raw-code fallback and a customer
 * read "Delivery: TNMP" on a plan card. The two missing ones are added here, and
 * `tduDisplayName` is now the ONLY way a TDU reaches a customer-facing string.
 *
 * Names are the utilities' own trading names, not their legal entity names
 * ("Oncor", not "Oncor Electric Delivery Company LLC") — a card has to be
 * scannable, and the legal name adds nothing a shopper is looking for.
 */
const TDU_DISPLAY_NAME: Readonly<Record<string, string>> = {
  ONCR: 'Oncor',
  CNP: 'CenterPoint Energy',
  'AEP-C': 'AEP Texas Central',
  'AEP-N': 'AEP Texas North',
  TNMP: 'Texas-New Mexico Power',
  LPL: 'Lubbock Power & Light',
};

/** A 5-digit ZIP (after trimming). Pure string test — no I/O. */
export function isValidZip(zip: string): boolean {
  return /^\d{5}$/u.test(zip.trim());
}

/** The TDU code serving a ZIP, or null when the ZIP is not in the demo map. */
export function resolveTduForZip(zip: string): string | null {
  return ZIP_TO_TDU[zip.trim()] ?? null;
}

/**
 * The customer-facing name for a TDU code.
 *
 * ⚠️ THE RAW-CODE FALLBACK IS A LAST RESORT, NOT A DESIGN. An ERCOT code on a
 * customer screen ("Delivery: ONCR") is jargon the shopper has no way to decode,
 * and it is what this function exists to prevent — but rendering NOTHING for an
 * unrecognised utility would be worse than rendering its code, so the fallback
 * stays. If you see a bare code in the UI, the fix is an entry in the map above,
 * never a change here.
 */
export function tduDisplayName(tdu: string): string {
  return TDU_DISPLAY_NAME[tdu.trim()] ?? tdu;
}

/** The catalogue plans whose delivery utility matches `tdu` (exact code match). */
export function filterPlansByTdu(plans: readonly PlanSummary[], tdu: string): PlanSummary[] {
  return plans.filter((plan) => plan.tdsp === tdu);
}
